<!--
  Copyright (C) 2023 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <PrimeDialog
    data-testid="create-workflow-popup"
    :header="$t('workflow.startWorkflow')"
    visible
    modal
    maximizable
    class="popup"
    :style="{ width: '70rem' }"
    @keyup.esc="closePopup(false)"
  >
    <template #closeicon>
      <PrimeButton
        data-testid="create-workflow-popup-close-button"
        icon="ri-close-line"
        :aria-label="$t('close')"
        outlined
        text
        severity="secondary"
        autofocus="false"
        @click="closePopup(false)"
      />
    </template>
    <PrimeSteps
      data-testid="create-workflow-popup-stepper"
      :model="stepItems"
      :active-step="activeStep"
      class="mb-4"
    />

    <div v-if="selectedFiles.length > 1" class="mb-3">
      <label class="font-bold">{{ $t("workflow.selectedDocuments") }}</label>
      <ul class="mt-2 pl-4">
        <li v-for="file in selectedFiles" :key="file.id">
          {{ file.name }}
        </li>
      </ul>
    </div>

    <!-- Step 1: Workflow Type -->
    <div v-show="activeStep === 0">
      <div class="flex flex-column h-12rem">
        <div class="flex flex-column gap-3">
          <!-- Section type workflow -->
          <div class="field flex flex-column gap-2">
            <label for="dueDate" class="font-bold"
              >{{ $t("workflow.chooseWorkflow")
              }}<span class="required">*</span></label
            >
            <PrimeSelect
              data-testid="create-workflow-popup-type-dropdown"
              v-model="selectedWorkflowType"
              :options="workflowOptions"
              option-label="label"
              :placeholder="$t('workflow.workflowType')"
            />
          </div>
        </div>
      </div>
      <div class="flex pt-4 justify-content-end">
        <PrimeButton
          data-testid="create-workflow-popup-step1-next-button"
          :label="$t('workflow.next')"
          icon="ri-arrow-right-line"
          icon-pos="right"
          @click="nextStep"
        />
      </div>
    </div>

    <!-- Step 2: Assignee Selection -->
    <div v-show="activeStep === 1" data-testid="create-workflow-popup-step2">
      <div class="flex flex-column">
        <div class="flex flex-column gap-3">
          <!-- Section assignee -->
          <div class="field flex flex-column gap-2">
            <label for="dueDate" class="font-bold"
              >{{ $t("workflow.assigneeSelection")
              }}<span class="required">*</span></label
            >
            <div v-if="assignee" class="card selection-card">
              Sélection de l'utilisateur
              {{ assignee.displayName || assignee.id }}
            </div>
          </div>
        </div>
        <div class="flex flex-column">
          <div class="flex flex-column gap-3">
            <PrimeDataTable
              data-testid="create-workflow-popup-users-table"
              id="users"
              v-model:selection="assignee"
              :value="usersList"
              selection-mode="single"
              row-hover
              data-key="id"
              removable-sort
              paginator
              paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              :rows="5"
              :loading="isSearching"
            >
              <template #empty>{{
                searchInput && searchInput.length >= 2
                  ? $t("usersEmpty")
                  : $t("workflow.typeToSearch")
              }}</template>
              <template #header>
                <div class="flex justify-content-between align-items-center">
                  <div v-if="workspaceId" class="flex align-items-center">
                    <PrimeCheckbox
                      v-model="workspaceMembersOnly"
                      input-id="workspace-members-only"
                      data-testid="create-workflow-popup-workspace-members-only"
                      binary
                      @change="onSearchInput"
                    />
                    <label for="workspace-members-only" class="ml-2">
                      {{ $t("workflow.workspaceMembersOnly") }}
                    </label>
                  </div>
                  <PrimeIconField icon-position="left">
                    <PrimeInputIcon class="ri-search-line" />
                    <PrimeInputText
                      data-testid="create-workflow-popup-users-search-input"
                      v-model="searchInput"
                      :placeholder="$t('search')"
                      @input="onSearchInput"
                    />
                  </PrimeIconField>
                </div>
              </template>
              <PrimeColumn field="avatar">
                <template #body="slotProps">
                  <PrimeAvatar
                    :label="slotProps.data.displayName.charAt(0)"
                    size="large"
                    class="avatar-blue"
                    shape="circle"
                  />
                </template>
              </PrimeColumn>
              <PrimeColumn field="name" :header="$t('column.name')" sortable>
                <template #body="slotProps">
                  <span>{{ slotProps.data.displayName }}</span>
                </template>
              </PrimeColumn>
              <PrimeColumn field="id" :header="$t('column.id')" sortable>
                <template #body="slotProps">
                  <span>{{ slotProps.data.id }}</span>
                </template>
              </PrimeColumn>
              <PrimeColumn field="email" :header="$t('column.email')" sortable>
                <template #body="slotProps">
                  <span>{{ slotProps.data.email }}</span>
                </template>
              </PrimeColumn>
            </PrimeDataTable>
          </div>
        </div>
      </div>
      <div class="flex pt-4 justify-content-between">
        <PrimeButton
          data-testid="create-workflow-popup-step2-back-button"
          :label="$t('workflow.back')"
          severity="secondary"
          icon="ri-arrow-left-line"
          @click="prevStep"
        />
        <PrimeButton
          data-testid="create-workflow-popup-step2-next-button"
          :label="$t('workflow.next')"
          icon="ri-arrow-right-line"
          icon-pos="right"
          @click="nextStep"
        />
      </div>
    </div>

    <!-- Step 3: Due Date and Priority -->
    <div v-show="activeStep === 2" data-testid="create-workflow-popup-step3">
      <div class="flex flex-column h-12rem">
        <div class="flex flex-column gap-3">
          <!-- Section Date d'échéance -->
          <div class="field flex flex-column gap-2">
            <label for="dueDate" class="font-bold">{{
              $t("workflow.dueDate")
            }}</label>
            <PrimeDatePicker
              data-testid="create-workflow-popup-duedate-calendar"
              id="dueDate"
              v-model="dueDate"
              date-format="dd/mm/yy"
              class="w-full"
              :placeholder="$t('workflow.chooseDueDate')"
              :min-date="minDate"
              :manual-input="false"
            />
          </div>

          <!-- Section Priorité -->
          <div class="flex flex-column gap-2">
            <label for="priority" class="font-bold"
              >{{ $t("workflow.priority.priority")
              }}<span class="required">*</span></label
            >
            <PrimeSelect
              data-testid="create-workflow-popup-priority-dropdown"
              id="priority"
              v-model="selectedPriority"
              :options="priorityOptions"
              option-label="label"
              :placeholder="$t('workflow.choosePriority')"
              class="w-full"
            />
          </div>
        </div>
      </div>
      <div class="flex pt-4 justify-content-between">
        <PrimeButton
          data-testid="create-workflow-popup-step3-back-button"
          :label="$t('workflow.back')"
          severity="secondary"
          icon="ri-arrow-left-line"
          @click="prevStep"
        />
        <PrimeButton
          data-testid="create-workflow-popup-step3-next-button"
          :label="$t('workflow.next')"
          icon="ri-arrow-right-line"
          icon-pos="right"
          @click="nextStep"
        />
      </div>
    </div>

    <!-- Step 4: Task Name -->
    <div v-show="activeStep === 3" data-testid="create-workflow-popup-step4">
      <div class="flex flex-column h-12rem">
        <label for="priority" class="font-bold"
          >{{ $t("workflow.taskName") }} <span class="required">*</span>
        </label>
        <PrimeTextArea
          data-testid="create-workflow-popup-message-textarea"
          v-model="message"
        />
      </div>
      <div>
        <PrimeMessage
          v-if="startWorkflowError"
          data-testid="create-workflow-popup-error-message"
          severity="error"
          :closable="false"
          >{{ failStartWorkflowErrorMessage }}</PrimeMessage
        >
      </div>
      <div class="flex pt-4 justify-content-between">
        <PrimeButton
          data-testid="create-workflow-popup-step4-back-button"
          :label="$t('workflow.back')"
          severity="secondary"
          icon="ri-arrow-left-line"
          @click="prevStep"
        />
        <PrimeButton
          data-testid="create-workflow-popup-submit-button"
          :disabled="isStartWorkflowButtonDisable"
          :label="$t('workflow.startTheWorkflow')"
          severity="primary"
          @click="startWorkflow()"
        />
      </div>
    </div>
  </PrimeDialog>
</template>

<script>
import { useSelectionStore } from "@/stores/selection.js";
import { useNavigationStore } from "@/stores/navigation.js";
import peopleService from "@/services/PeopleService.js";
import workflowService from "@/services/WorkflowService.js";
import workspaceService from "@/services/WorkspaceService.js";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Steps from "primevue/steps";
import Select from "primevue/select";
import Avatar from "primevue/avatar";
import InputText from "primevue/inputtext";
import Dialog from "primevue/dialog";
import IconField from "primevue/iconfield";
import DatePicker from "primevue/datepicker";
import Textarea from "primevue/textarea";
import InputIcon from "primevue/inputicon";
import Message from "primevue/message";

export default {
  name: "CreateWorkflowPopup",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
    PrimeSelect: Select,
    PrimeSteps: Steps,
    PrimeAvatar: Avatar,
    PrimeInputText: InputText,
    PrimeIconField: IconField,
    PrimeDatePicker: DatePicker,
    PrimeTextArea: Textarea,
    PrimeInputIcon: InputIcon,
    PrimeCheckbox: Checkbox,
    PrimeMessage: Message,
  },
  emits: ["close", "message"],
  data() {
    return {
      activeStep: 0,
      startWorkflowError: false,
      failStartWorkflowErrorMessage: null,
      workflowOptions: [
        {
          label: this.$t("workflow.adhocType"),
          description: this.$t("workflow.adhocDescription"),
          value: "activitiAdhoc",
        },
        {
          label: this.$t("workflow.reviewType"),
          description: this.$t("workflow.reviewDescription"),
          value: "activitiReview",
        },
      ],
      priorityOptions: [
        {
          label: this.$t("workflow.priority.low"),
          value: 3,
        },
        {
          label: this.$t("workflow.priority.medium"),
          value: 2,
        },
        {
          label: this.$t("workflow.priority.high"),
          value: 1,
        },
      ],
      selectedWorkflowType: null,
      message: null,
      assignee: null,
      dueDate: null,
      selectedPriority: {
        label: this.$t("workflow.priority.medium"),
        value: 2,
      },
      workspaceMembersOnly: true,
      searchInput: null,
      isSearching: false,
      searchTimeout: null,
      usersList: [],
      minDate: new Date(new Date().setHours(24, 0, 0, 0)),
    };
  },
  computed: {
    workspaceId() {
      return useNavigationStore().workspace?.id || null;
    },
    stepItems() {
      return [
        { label: this.$t("workflow.workflowType") },
        { label: this.$t("workflow.assigneeSelection") },
        { label: this.$t("workflow.dueDateAndPrioritySelection") },
        { label: this.$t("workflow.taskNameInput") },
      ];
    },
    selectedFiles() {
      return useSelectionStore().files;
    },
    isStartWorkflowButtonDisable() {
      return (
        this.selectedWorkflowType === null ||
        this.assignee === null ||
        this.selectedPriority === null ||
        this.message === null
      );
    },
  },
  mounted() {
    if (this.workspaceId) {
      this.loadWorkspaceMembers();
    }
  },
  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    nextStep() {
      if (this.activeStep < 3) {
        this.activeStep++;
      }
    },
    prevStep() {
      if (this.activeStep > 0) {
        this.activeStep--;
      }
    },
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      const term = (this.searchInput || "").trim();
      if (term.length < 2) {
        if (this.workspaceMembersOnly && this.workspaceId) {
          this.loadWorkspaceMembers();
        } else {
          this.usersList = [];
        }
        return;
      }
      this.searchTimeout = setTimeout(() => {
        this.searchUsers(term);
      }, 300);
    },
    loadWorkspaceMembers() {
      this.isSearching = true;
      workspaceService
        .getWorkspaceMemberships(this.workspaceId)
        .then((members) => {
          this.usersList = members
            .map((m) => ({
              id: m.id,
              displayName:
                `${m.person.firstName || ""} ${m.person.lastName || ""}`.trim(),
              email: m.person.email || "",
            }))
            .filter(
              (user) =>
                !["publication", "pastell"].some((hidden) =>
                  user.id.includes(hidden),
                ),
            );
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message,
            life: 3000,
          });
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    searchUsers(term) {
      this.isSearching = true;
      peopleService
        .searchUser(term)
        .then((response) => {
          const entries = response.list.entries || [];
          let users = entries
            .map((entry) => {
              const props = entry.entry.properties;
              return {
                id: props["cm:userName"],
                displayName:
                  `${props["cm:firstName"] || ""} ${props["cm:lastName"] || ""}`.trim(),
                email: props["cm:email"] || "",
              };
            })
            .filter(
              (user) =>
                !["publication", "pastell"].some((hidden) =>
                  user.id.includes(hidden),
                ),
            );

          if (this.workspaceMembersOnly && this.workspaceId) {
            return workspaceService
              .getWorkspaceMemberships(this.workspaceId)
              .then((members) => {
                const memberIds = new Set(members.map((m) => m.id));
                this.usersList = users.filter((user) => memberIds.has(user.id));
              });
          } else {
            this.usersList = users;
          }
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message,
            life: 3000,
          });
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    closePopup(reload) {
      this.$emit("close", reload);
    },
    startWorkflow() {
      if (this.message.length >= 4000) {
        ((this.startWorkflowError = true),
          (this.failStartWorkflowErrorMessage = this.$t(
            "workflow.tooLongTaskNameError",
          )));
      }
      const opts = {
        processDefinitionKey: this.selectedWorkflowType.value,
        variables: {
          bpm_assignee: this.assignee.id,
          bpm_workflowDescription: this.message,
          bpm_description: this.message,
          bpm_priority: this.selectedPriority.value,
          bpm_workflowPriority: this.selectedPriority.value,
          ...(this.dueDate && {
            bpm_workflowDueDate: this.dueDate
              .toISOString()
              .replace("Z", "+0000"),
          }),
        },
      };
      workflowService
        .createProcess(opts)
        .then((response) => {
          let processId = response.data.entry.id;
          const opts = useSelectionStore().files.map((file) => ({
            id: file.id,
          }));
          workflowService
            .addProcessItems(processId, opts)
            .then(() => {
              this.$toast.add({
                severity: "success",
                summary: this.$t("success"),
                detail: this.$t("workflow.successCreate"),
                life: 3000,
              });
              this.$emit("close");
            })
            .catch((error) => {
              this.$toast.add({
                severity: "error",
                summary: this.$t("error"),
                detail: error.message,
                life: 3000,
              });
            });
        })
        .catch((error) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: error.message,
            life: 3000,
          });
        });
    },
  },
};
</script>
<style lang="css" scoped>
.selection-card {
  background: var(--surface-section);
}

.avatar-blue {
  background-color: #2196f3;
  color: #ffffff;
}
</style>
